import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { Ayuda } from '../components/Ayuda';
import { CargaPagina } from '../components/Spinner';
import { PanelLateral } from '../components/PanelLateral';
import { ConectarMicrosoft } from './NavegadorSharePoint';
import { type Favorito } from './dia/tipos';
import { ExploradorSharePoint } from './dia/ExploradorSharePoint';
import { FilaDia } from './dia/FilaDia';
import { ModalAnalisis } from './dia/ModalAnalisis';

/**
 * Agente DIA (rediseño 2026-10-22, spec docs/superpowers/specs/2026-10-22-agente-dia-ficha-y-rediseno-design.md):
 * UNA página = UN trabajo. Solo el listado compacto de DIAs en trabajo; "+ Agregar DIA" abre el
 * explorador SharePoint en un panel lateral y "Entrenamiento" (Biblioteca, casos de referencia,
 * oficio) es una página aparte. Abrir una DIA lleva a /agentes/dia/:id (Chat | Capítulos).
 */

const AYUDA_PAGINA = 'Tus DIAs en trabajo. Cada fila dice cuánto de la carpeta ya estudió Andrés y qué falta. "Abrir" entra al proyecto; "+ Agregar DIA" trae una carpeta de SharePoint; "Entrenamiento" es lo que Andrés sabe para todas las DIA.';

export default function AgenteDia() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [favoritos, setFavoritos] = useState<Favorito[]>([]);
  const [verAnalisisId, setVerAnalisisId] = useState<string | null>(null);
  const [agregando, setAgregando] = useState(false);
  const [filtro, setFiltro] = useState('');

  const [requiereMs, setRequiereMs] = useState(false);
  // Primera carga de proyectos: con Wave 3 verifica el acceso real de cada carpeta contra
  // SharePoint (tu token), así que puede tardar unos segundos ⇒ spinner de página.
  const [favoritosCargando, setFavoritosCargando] = useState(true);
  const cargarFavoritos = useCallback(async () => {
    try {
      const d = await apiFetch<{ favoritos: Favorito[]; requiere_ms?: boolean }>('/api/sharepoint/favoritos');
      setFavoritos(d.favoritos);
      setRequiereMs(!!d.requiere_ms);
    } catch { /* accesorio */ }
    finally { setFavoritosCargando(false); }
  }, []);

  useEffect(() => { void cargarFavoritos(); }, [cargarFavoritos]);

  // Refresco automático: cada 4 s mientras algún proyecto está en estudio, y durante 2 minutos después de
  // cualquier acción (Sincronizar, Preparar, Fase 2) aunque el worker aún no haya cambiado el estado.
  const [pollHasta, setPollHasta] = useState(0);
  const activo = favoritos.some((f) => ['ingestando', 'analizando', 'enriqueciendo'].includes(f.sync_estado ?? ''));
  useEffect(() => {
    if (!activo && Date.now() > pollHasta) return;
    const t = window.setInterval(() => { if (!activo && Date.now() > pollHasta) return; void cargarFavoritos(); }, 4000);
    return () => window.clearInterval(t);
  }, [activo, pollHasta, cargarFavoritos]);
  const trasAccion = useCallback(async () => { setPollHasta(Date.now() + 120_000); await cargarFavoritos(); }, [cargarFavoritos]);

  async function lanzarEstudio(id: string) {
    try {
      await apiFetch(`/api/sharepoint/favoritos/${id}/estudiar`, { method: 'POST' });
      setVerAnalisisId(null);
      await cargarFavoritos();
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo lanzar el estudio.'); }
  }

  // Wave 3: sin cuenta Microsoft conectada no hay proyectos DIA (permisos heredados).
  if (requiereMs) return <ConectarMicrosoft contexto="Los proyectos DIA y el chat con Andrés funcionan" />;

  // Primera carga: verificar el acceso a cada proyecto contra SharePoint tarda unos
  // segundos — spinner de página en vez de mostrar la vista vacía.
  if (favoritosCargando) return <CargaPagina mensaje="Cargando tus proyectos DIA" />;

  const todas = favoritos.filter((f) => f.tipo !== 'referencia');
  const trabajos = todas.filter((f) => f.nombre.toLowerCase().includes(filtro.trim().toLowerCase()));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[13px] font-normal text-gris">
          Andrés, el Consultor · DIAs en trabajo ( {todas.length} )
          <Ayuda texto={AYUDA_PAGINA} />
        </p>
        <div className="flex gap-2">
          <Ayuda texto="Lo que Andrés sabe para TODAS las DIA: Biblioteca AMBEC, casos de referencia (incluido el e-SEIA) y su oficio." toque={false}>
            <Link to="/agentes/dia/entrenar" className="btn-secundario !h-9">🎓 Entrenamiento ↗</Link>
          </Ayuda>
          <Ayuda texto="Trae una carpeta de SharePoint como DIA en trabajo. Andrés la analiza y estudia sus documentos." toque={false}>
            <button type="button" onClick={() => setAgregando(true)} className="btn-primario !h-9">+ Agregar DIA</button>
          </Ayuda>
        </div>
      </div>

      {error && <p role="alert" className="alerta-error">{error}</p>}

      <section className="tarjeta px-4 py-2">
        {todas.length > 5 && (
          <input value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Buscar DIA…" aria-label="Buscar DIA por nombre" className="campo my-2 !h-8 !text-[12.5px]" />
        )}
        {trabajos.length === 0 ? (
          <p className="py-10 text-center text-sm font-light text-gris">
            {filtro ? 'Ninguna DIA coincide con la búsqueda.' : 'Aún no tienes DIAs en trabajo. Usa "+ Agregar DIA" para traer la carpeta de un proyecto.'}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {trabajos.map((f) => (
              <FilaDia key={f.id} f={f} onAbrir={() => navigate(`/agentes/dia/${f.id}`)} onAnalisis={() => setVerAnalisisId(f.id)} onCambio={trasAccion} onError={setError} />
            ))}
          </ul>
        )}
      </section>

      {agregando && (
        <PanelLateral titulo="Agregar DIA" ayuda="Desde aquí incorporas un proyecto de SharePoint. Al marcarlo, Andrés analiza la carpeta y te muestra una estimación antes de estudiarla." onCerrar={() => setAgregando(false)}>
          <ExploradorSharePoint favoritos={favoritos} onMarcado={async (id) => { setAgregando(false); await cargarFavoritos(); setVerAnalisisId(id); }} />
        </PanelLateral>
      )}

      {verAnalisisId && (() => { const f = favoritos.find((x) => x.id === verAnalisisId); return f ? (
        <ModalAnalisis f={f} onCerrar={() => setVerAnalisisId(null)} onEstudiar={lanzarEstudio}
          onQuitar={async (id) => { await apiFetch(`/api/sharepoint/favoritos/${id}`, { method: 'DELETE' }); setVerAnalisisId(null); await cargarFavoritos(); }} />
      ) : null; })()}
    </div>
  );
}
